import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import api from '../services/api'

/**
 * Shared composable for Basecamp OAuth popup flow.
 * Used by ConnectServices, PhaseDetail, and DevPanel.
 *
 * @param {Object} options
 * @param {Function} [options.onSuccess] - Called after successful connect + user refresh
 * @param {Function} [options.onError] - Called with error message on failure
 */
export function useBasecampOAuth({ onSuccess, onError } = {}) {
  const store = useStore()

  const connecting = ref(false)
  const error = ref(null)

  const isConnected = computed(() => store.getters['auth/isBasecampConnected'])
  const accounts = computed(() => store.getters['auth/basecampAccounts'])

  const connect = async () => {
    connecting.value = true
    error.value = null
    try {
      const response = await api.get('/oauth/basecamp/start')
      if (response.data.success) {
        const popup = window.open(response.data.data.authUrl, 'basecamp-oauth', 'width=600,height=700')
        if (!popup) {
          error.value = 'Popup blocked — please allow popups for this site and try again.'
          connecting.value = false
        }
      } else {
        error.value = response.data.error || 'Failed to start Basecamp connection.'
        connecting.value = false
      }
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to start Basecamp connection.'
      connecting.value = false
      if (onError) onError(error.value)
    }
  }

  const disconnect = async () => {
    try {
      await api.post('/oauth/basecamp/disconnect')
      await store.dispatch('auth/fetchUser')
    } catch (err) {
      console.error('Failed to disconnect Basecamp:', err)
    }
  }

  // Listen for OAuth popup result via localStorage (cross-origin-safe)
  const onStorage = async (event) => {
    if (event.key !== 'oauth-callback-result' || !event.newValue) return
    let result
    try {
      result = JSON.parse(event.newValue)
    } catch { return }
    localStorage.removeItem('oauth-callback-result')

    // Ignore stale results (older than 60 seconds)
    if (result.timestamp && Date.now() - result.timestamp > 60000) return

    connecting.value = false

    if (result.success) {
      await store.dispatch('auth/fetchUser')
      if (onSuccess) onSuccess()
    } else {
      error.value = result.error || 'Basecamp connection failed.'
      if (onError) onError(error.value)
    }
  }

  onMounted(() => window.addEventListener('storage', onStorage))
  onUnmounted(() => window.removeEventListener('storage', onStorage))

  return {
    connecting,
    error,
    isConnected,
    accounts,
    connect,
    disconnect
  }
}
